import {
  Action,
  ActionType,
  Duration,
  DurationUnit,
  PauseAt,
  PaymentStatus,
  Subscription,
  SubscriptionStatus,
} from '@wix/ambassador-billing-v1-subscription/types';
import { addDays, addWeeks, addMonths, addYears, startOfDay, differenceInCalendarDays } from 'date-fns';
import { ECOM_APP_DEF_ID, NEW_STORES_APP_DEF_ID, STORES_APP_DEF_ID } from './constants';

export const isRecurringSubscription = (subscription: Subscription) =>
  subscription?.billingSettings &&
  (subscription.billingSettings?.totalCycles === undefined ||
    (subscription.billingSettings?.totalCycles && subscription.billingSettings?.totalCycles > 1));

export const isPendingCancellationSubscription = (subscription: Subscription) =>
  isRecurringSubscription(subscription) && subscription.billingSettings?.cycleAutoRenew === false;

export const getCancelActionType = (
  subscription: Subscription,
  allowedActions: Action[] | undefined,
  enableSkipSubscriptions: boolean,
) => {
  const isAllowed = (actionType: ActionType) => !!allowedActions?.some((action) => action.actionType === actionType);
  if (!isRecurringSubscription(subscription)) {
    return isAllowed(ActionType.CANCEL) ? ActionType.CANCEL : undefined;
  }
  if (isAllowed(ActionType.CYCLE_AUTO_RENEW_OFF)) {
    return ActionType.CYCLE_AUTO_RENEW_OFF;
  }
  return enableSkipSubscriptions && isAllowed(ActionType.CANCEL) ? ActionType.CANCEL : undefined;
};

export const hasAnyDiscount = (subscription: Subscription) => {
  if (subscription.billingSettings?.discounts && subscription.billingSettings?.discounts.length > 0) {
    return true;
  }

  const hasItemWithDiscount = !!subscription.items?.find((item) => item.discounts && item.discounts.length > 0);
  return hasItemWithDiscount;
};

const isStoresAppId = (appId?: string | null) => appId === ECOM_APP_DEF_ID || appId === NEW_STORES_APP_DEF_ID;

export const isStoresSubscription = (subscription: Subscription) =>
  isStoresAppId(subscription.origin?.appId) &&
  subscription.platformOrigin?.appId === ECOM_APP_DEF_ID &&
  !!subscription.items?.length &&
  subscription.items.every((item) => isStoresAppId(item.catalogReference?.appId));

export const isStoresSubscriptionOrLegacy = (subscription: Subscription, enableSkipSubscriptions: boolean) =>
  subscription.origin?.appId === STORES_APP_DEF_ID || (enableSkipSubscriptions && isStoresSubscription(subscription));

export const getCustomerSettingsAppId = (subscription: Subscription) => {
  if (isStoresSubscription(subscription)) {
    return NEW_STORES_APP_DEF_ID;
  }
  const originAppId = subscription.origin?.appId;
  return isStoresAppId(originAppId) ? undefined : originAppId;
};

const isSkipPause = (subscription: Subscription) =>
  subscription.pauseInfo?.pauseAt === PauseAt.NUMBER_OF_CYCLES_FROM_NEXT_BILLING_DATE;

export const hasScheduledSkip = (subscription: Subscription) =>
  subscription.status === SubscriptionStatus.ACTIVE && isSkipPause(subscription);

export const isCurrentlySkipping = (subscription: Subscription) =>
  subscription.status === SubscriptionStatus.PAUSED && isSkipPause(subscription);

export const addCyclesFromAnchor = (anchor: Date, cycleCount: number, cycleDuration: Duration): Date => {
  const totalUnits = (cycleDuration.count ?? 1) * cycleCount;
  switch (cycleDuration.unit) {
    case DurationUnit.DAY:
      return addDays(anchor, totalUnits);
    case DurationUnit.WEEK:
      return addWeeks(anchor, totalUnits);
    case DurationUnit.MONTH:
      return addMonths(anchor, totalUnits);
    case DurationUnit.YEAR:
      return addYears(anchor, totalUnits);
    default:
      return anchor;
  }
};

export const toDateOnly = (d: Date): Date => startOfDay(d);

export const daysBetween = (from: Date, to: Date): number => differenceInCalendarDays(to, from);

export const calculateSkipCycles = (
  selectedStartDate: Date,
  selectedEndDate: Date,
  nextBillingDate: Date,
  cycleDuration: Duration,
): { pauseAtCycles: number; cyclesToSkip: number; resumeAtCycles: number } => {
  const start = toDateOnly(selectedStartDate);
  const end = toDateOnly(selectedEndDate);
  const anchor = toDateOnly(nextBillingDate);
  const maxCyclesToScan = Math.ceil(365 / durationInDays(cycleDuration)) + 2;

  let pauseAtCycles = 1;
  while (
    pauseAtCycles <= maxCyclesToScan &&
    toDateOnly(addCyclesFromAnchor(anchor, pauseAtCycles - 1, cycleDuration)) < start
  ) {
    pauseAtCycles++;
  }

  let resumeAtCycles = pauseAtCycles;
  while (
    resumeAtCycles <= pauseAtCycles + maxCyclesToScan &&
    toDateOnly(addCyclesFromAnchor(anchor, resumeAtCycles - 1, cycleDuration)) <= end
  ) {
    resumeAtCycles++;
  }

  const cyclesToSkip = Math.max(resumeAtCycles - pauseAtCycles, 1);
  return { pauseAtCycles, cyclesToSkip, resumeAtCycles: pauseAtCycles + cyclesToSkip };
};

export const isWithinSkipNoticeWindow = (
  date: Date,
  nextBillingDate: Date,
  cycleDuration: Duration,
  noticeDays: number,
): boolean => {
  if (noticeDays <= 0) {
    return false;
  }
  const d = toDateOnly(date);
  const maxCyclesToScan = Math.ceil(365 / durationInDays(cycleDuration)) + 1;
  for (let k = 0; k < maxCyclesToScan; k++) {
    const billingDate = addCyclesFromAnchor(nextBillingDate, k, cycleDuration);
    const diff = daysBetween(d, billingDate);
    if (diff < 0) {
      continue;
    }
    return diff < noticeDays;
  }
  return false;
};

export const exceedsSkipsLeft = (
  startDate: Date,
  endDate: Date,
  nextBillingDate: Date,
  cycleDuration: Duration,
  skipsLeft: number,
): boolean => calculateSkipCycles(startDate, endDate, nextBillingDate, cycleDuration).cyclesToSkip > skipsLeft;

export const toDate = (d: Date | string | null | undefined): Date | undefined =>
  d ? (typeof d === 'string' ? new Date(d) : d) : undefined;

export const getSkipCyclesCount = (subscription: Subscription): number => {
  const startCycle = subscription.pauseInfo?.startCycle ?? 0;
  const resumeCycle = subscription.pauseInfo?.resumeCycle ?? 0;
  return Math.max(resumeCycle - startCycle, 1);
};

export const getUsedSkipCount = (subscription: Subscription): number =>
  subscription.policies?.customerActionCounters?.skipCount ?? 0;

export const getSkipsLeft = (subscription: Subscription, maxUserActions: number): number =>
  Math.max(maxUserActions - getUsedSkipCount(subscription), 0);

const durationInDays = (cycleDuration: Duration): number => {
  const count = cycleDuration.count ?? 1;
  switch (cycleDuration.unit) {
    case DurationUnit.DAY:
      return count;
    case DurationUnit.WEEK:
      return count * 7;
    case DurationUnit.MONTH:
      return count * 30;
    case DurationUnit.YEAR:
      return count * 365;
    default:
      return count;
  }
};

export const isStartNoticeBeyondCycle = (subscription: Subscription, minStartNoticeDays: number): boolean => {
  const cycleDuration = subscription.billingSettings?.cycleDuration;
  if (!cycleDuration) {
    return false;
  }
  return minStartNoticeDays >= durationInDays(cycleDuration);
};

export const getBillingCycleDate = (subscription: Subscription, cycle: number): Date | undefined => {
  const cycleDuration = subscription.billingSettings?.cycleDuration;
  const currentCycle = subscription.billingStatus?.currentCycle;
  if (!cycleDuration || currentCycle == null) {
    return undefined;
  }
  const previousBillingDate = toDate(subscription.billingStatus?.previousBillingDate);
  if (previousBillingDate) {
    return addCyclesFromAnchor(previousBillingDate, cycle - currentCycle, cycleDuration);
  }
  const nextBillingDate = toDate(subscription.billingStatus?.nextBillingDate);
  if (nextBillingDate) {
    return addCyclesFromAnchor(nextBillingDate, cycle - (currentCycle + 1), cycleDuration);
  }
  return undefined;
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MAX_BOUNDARY_LOOKAHEAD = 120;

export const nextCycleBoundaryAfter = (subscription: Subscription, from: Date): Date | undefined => {
  const cycleDuration = subscription.billingSettings?.cycleDuration;
  const anchor =
    toDate(subscription.billingStatus?.nextBillingDate) ?? toDate(subscription.billingStatus?.previousBillingDate);
  if (!cycleDuration || !(cycleDuration.count ?? 1) || !anchor) {
    return undefined;
  }
  for (let k = 0; k <= MAX_BOUNDARY_LOOKAHEAD; k++) {
    const boundary = addCyclesFromAnchor(anchor, k, cycleDuration);
    if (boundary.getTime() > from.getTime()) {
      return boundary;
    }
  }
  return undefined;
};

const isNoticeMet = (anchor: Date | undefined, noticeDays: number, now: Date): boolean =>
  noticeDays <= 0 || !anchor || Math.trunc((anchor.getTime() - now.getTime()) / MS_PER_DAY) >= noticeDays;

const cycleDateFromNextBilling = (subscription: Subscription, cycle: number): Date | undefined => {
  const cycleDuration = subscription.billingSettings?.cycleDuration;
  const currentCycle = subscription.billingStatus?.currentCycle;
  const nextBillingDate = toDate(subscription.billingStatus?.nextBillingDate);
  if (!cycleDuration || currentCycle == null || !nextBillingDate || cycle <= currentCycle) {
    return undefined;
  }
  return addCyclesFromAnchor(nextBillingDate, cycle - (currentCycle + 1), cycleDuration);
};

const getRevocationAnchor = (subscription: Subscription): Date | undefined => {
  if (subscription.status === SubscriptionStatus.PAUSED) {
    return undefined;
  }
  const startCycle = subscription.pauseInfo?.startCycle;
  return (
    toDate(subscription.pauseInfo?.startDate) ??
    (startCycle != null ? cycleDateFromNextBilling(subscription, startCycle) : undefined) ??
    toDate(subscription.billingStatus?.nextBillingDate)
  );
};

export const isResumeTooClose = (subscription: Subscription, minEarlyEndNoticeDays: number, now: Date): boolean =>
  !isNoticeMet(
    nextCycleBoundaryAfter(subscription, now) ?? toDate(subscription.billingStatus?.nextBillingDate),
    minEarlyEndNoticeDays,
    now,
  );

export const isUnskipTooClose = (subscription: Subscription, minRevocationNoticeDays: number, now: Date): boolean =>
  !isNoticeMet(getRevocationAnchor(subscription), minRevocationNoticeDays, now);

export const isNextBillingWithinNotice = (subscription: Subscription, noticeDays: number, now: Date): boolean =>
  !isNoticeMet(toDate(subscription.billingStatus?.nextBillingDate), noticeDays, now);

export const getLastCycleDate = (subscription: Subscription): Date | undefined => {
  const totalCycles = subscription.billingSettings?.totalCycles;
  if (!totalCycles) {
    return undefined;
  }
  return getBillingCycleDate(subscription, totalCycles);
};

export const hasSkippableCycle = (subscription: Subscription): boolean => {
  const nextBillingDate = toDate(subscription.billingStatus?.nextBillingDate);
  if (!nextBillingDate) {
    return false;
  }
  const lastCycleDate = getLastCycleDate(subscription);
  return !lastCycleDate || toDateOnly(nextBillingDate) <= toDateOnly(lastCycleDate);
};

export const isInFreeTrial = (subscription: Subscription): boolean => {
  if (subscription.status !== SubscriptionStatus.ACTIVE || !subscription.billingSettings?.freeTrialDuration) {
    return false;
  }
  const freeTrialEnd = toDate(subscription.billingStatus?.freeTrialData?.endDate);
  return !!freeTrialEnd && freeTrialEnd.getTime() > new Date().getTime();
};

export const shouldShowSkipCta = (subscription: Subscription, minStartNoticeDays: number): boolean =>
  subscription.status === SubscriptionStatus.ACTIVE &&
  !hasScheduledSkip(subscription) &&
  !isCurrentlySkipping(subscription) &&
  !isStartNoticeBeyondCycle(subscription, minStartNoticeDays) &&
  !isInFreeTrial(subscription) &&
  !isInGracePeriod(subscription) &&
  hasSkippableCycle(subscription);

export const isInGracePeriod = (subscription: Subscription | undefined) => {
  return !!(
    subscription &&
    subscription.billingStatus?.gracePeriodData &&
    subscription.billingStatus?.latestPaymentData?.paymentStatus === PaymentStatus.FAILED &&
    subscription.status !== SubscriptionStatus.CANCELED
  );
};
